import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { WebPageSchema, OrganizationSchema, BreadcrumbListSchema, FAQPageSchema } from '@/components/base/SchemaOrg';


export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const faqs = [
    {
      question: '酷漫屋是什么？',
      answer: '酷漫屋是一个专注于韩国漫画在线阅读的平台，提供数千部免费韩漫资源。我们支持下拉式阅读模式，无需翻页即可流畅浏览漫画内容。所有资源永久免费，更新速度快，是韩漫爱好者首选的在线阅读社区。',
    },
    {
      question: '酷漫屋的漫画来源合法吗？',
      answer: '酷漫屋的所有漫画资源均来自网友分享和公开网络资源，仅供学习和交流使用。我们尊重版权，如涉及版权问题，请通过联系我们页面与我们取得联系，我们将第一时间处理。',
    },
    {
      question: '需要注册账号吗？',
      answer: '目前酷漫屋无需注册即可畅读所有漫画。我们致力于提供最低门槛的阅读体验，让每一位访客都能无障碍享受韩漫的乐趣。未来我们将推出账号系统，为用户提供收藏、历史记录等更多个性化功能。',
    },
    {
      question: '漫画多久更新一次？',
      answer: '我们与韩国各大漫画平台保持同步，热门连载漫画通常每周更新1-2话。您可以在首页的「最近更新」板块找到最新上线的章节。我们也在不断优化更新机制，争取更快的同步速度。',
    },
  ];

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9oskugsenbniolesl4g', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' },
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || responseText
          || '提交失败，请重试';
        setFormError(msg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <WebPageSchema
        title="关于我们_酷漫屋_韩漫免费在线阅读平台"
        description="酷漫屋品牌介绍 - 提供海量免费韩国漫画下拉式在线阅读服务。了解我们的品牌故事、平台优势及联系方式。"
        url="https://www.kumanwu.com/about"
      />
      <OrganizationSchema />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: 'https://www.kumanwu.com/' },
          { name: '关于我们', item: 'https://www.kumanwu.com/about' },
        ]}
      />
      <FAQPageSchema questions={faqs} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <section className="w-full py-14 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于酷漫屋
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                我们是一个热爱韩漫的团队，致力于为广大漫画爱好者打造最优质的在线阅读平台
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="w-full py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row items-center gap-12">
                <div className="flex-1">
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-5">
                    <strong>酷漫屋</strong>品牌介绍
                  </h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    酷漫屋成立于2023年，由一群热爱韩国漫画的开发者共同创建。我们发现许多韩漫爱好者在国内找不到一个资源全面、阅读体验优秀的平台，于是决定打造酷漫屋——一个专注于<strong>韩国漫画</strong>的<strong>免费在线阅读</strong>社区。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    平台上线以来，我们始终坚持「免费、高清、更新快」的核心理念。截至目前，酷漫屋已收录超过<strong>2,000部韩漫</strong>作品，涵盖恋爱、奇幻、动作、恐怖、剧情等十几个分类，累计服务超过<strong>50万</strong>日活跃用户。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    我们的团队不断优化阅读体验，率先推出<strong>下拉式阅读模式</strong>，让用户在手机上也能享受如瀑布般流畅的阅读体验。未来，我们将继续丰富漫画资源，优化产品功能，为每一位韩漫爱好者提供更好的服务。
                  </p>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[400px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20cozy%20reading%20room%20with%20comic%20books%20collection%2C%20warm%20lighting%2C%20comfortable%20sofa%2C%20plants%2C%20minimalist%20interior%20design%2C%20Korean%20aesthetic%2C%20peaceful%20atmosphere%2C%20clean%20composition&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="酷漫屋品牌 - 韩漫阅读"
                    className="w-full rounded-lg object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Reputation / Stats */}
          <section className="w-full py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-12">
                数据说明一切，用户的信任是我们前行的动力
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '2,000+', label: '收录漫画', icon: 'ri-book-2-line' },
                  { value: '50万+', label: '日活跃用户', icon: 'ri-user-smile-line' },
                  { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
                  { value: '4.9/5', label: '用户评分', icon: 'ri-star-line' },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <p className="font-heading text-2xl font-bold text-foreground-950 mb-1">{stat.value}</p>
                    <p className="text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="w-full py-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-10">
                发展历程
              </h2>
              <div className="flex flex-col gap-6">
                {[
                  { year: '2023年6月', title: '酷漫屋正式上线', desc: '首版网站上线，收录500+部韩漫资源，支持基础在线阅读功能。' },
                  { year: '2023年12月', title: '用户突破10万', desc: '凭借优质的漫画资源和流畅的阅读体验，日活跃用户突破10万。' },
                  { year: '2024年6月', title: '下拉式阅读上线', desc: '推出创新的下拉式阅读模式，大幅提升移动端阅读体验。' },
                  { year: '2024年12月', title: '漫画资源突破2000部', desc: '平台漫画资源突破2000部，覆盖几乎所有韩漫热门类型。' },
                  { year: '2025年6月', title: '全面改版升级', desc: '网站全面改版，优化UI设计和加载速度，日活跃用户突破50万。' },
                  { year: '2026年', title: '持续进步中', desc: '我们仍在不断优化产品，计划推出更多个性化功能，为韩漫爱好者带来更好的体验。' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="flex-shrink-0 w-24 text-right">
                      <span className="text-xs font-semibold text-primary-500 bg-primary-50 px-2 py-1 rounded whitespace-nowrap">
                        {item.year}
                      </span>
                    </div>
                    <div className="flex-1 pb-6 border-l-2 border-primary-200 pl-6 relative">
                      <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-primary-500 border-2 border-background-50"></div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full py-16 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="flex flex-col gap-3">
                {faqs.map((faq, i) => (
                  <details key={i} className="group rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full py-16">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                有任何问题或建议，欢迎随时与我们联系
              </p>

              {formState === 'success' ? (
                <div className="text-center p-8 rounded-lg bg-primary-50 border border-primary-200">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className="ri-check-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复您。</p>
                  <button
                    onClick={() => setFormState('idle')}
                    className="mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-5">
                  {formState === 'error' && (
                    <div className="p-4 rounded-lg bg-primary-50 border border-primary-200 text-sm text-primary-700">
                      {formError || '提交失败，请重试'}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-700">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <span className="text-xs text-foreground-400">最多500个字符</span>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap self-start"
                  >
                    提交留言
                  </button>
                </form>
              )}

              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                  <a href="mailto:contact@kumanwu.com" className="text-xs text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap" rel="nofollow">
                    contact@kumanwu.com
                  </a>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                    <i className="ri-timer-line text-lg"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                  <p className="text-xs text-foreground-500">周一至周日 9:00 - 22:00</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                    <i className="ri-message-3-line text-lg"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">反馈建议</h4>
                  <p className="text-xs text-foreground-500">通过上方表单提交</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}